import { ExclamationCircleOutlined } from '@ant-design/icons'
import { useQueryClient } from '@tanstack/react-query'
import { Button, Flex, message, Modal } from 'antd'
import { PropertyActivityItem } from 'Modules'
import { useState } from 'react'

import { AppAdmin } from 'api/app-admin'
import { logError } from 'utils/logger'

interface DeleteActivityModalProps {
  open: boolean
  activity: PropertyActivityItem | null
  ptoId: string
  onClose: () => void
}

export const DeleteActivityModal = ({
  open,
  activity,
  ptoId,
  onClose,
}: DeleteActivityModalProps) => {
  const queryClient = useQueryClient()
  const [loading, setLoading] = useState(false)

  const handleDelete = async () => {
    if (!activity) return
    try {
      setLoading(true)

      await AppAdmin.deletePropertyActivity(activity._id)

      message.success('Activity deleted successfully')
      queryClient.invalidateQueries({ queryKey: ['propertyActivities', 'activities', ptoId] })
      queryClient.invalidateQueries({ queryKey: ['propertyActivities', 'ptos'] })
      queryClient.invalidateQueries({ queryKey: ['propertyActivities', 'pto', ptoId] })
      onClose()
    } catch (e: any) {
      message.error(e?.message || 'Delete activity failed')
      logError(e)
    } finally {
      setLoading(false)
    }
  }

  return (
    <Modal
      open={open}
      title={
        <Flex gap={8} align="start">
          <ExclamationCircleOutlined style={{ color: '#faad14', fontSize: 20, marginTop: 2 }} />
          <Flex vertical>
            <div>Delete Activity</div>
            <div style={{ fontSize: 12, color: '#6a7282', fontWeight: 400 }}>
              This action cannot be undone
            </div>
          </Flex>
        </Flex>
      }
      onCancel={onClose}
      closable={!loading}
      maskClosable={!loading}
      centered
      destroyOnClose
      footer={() => (
        <>
          <Button disabled={loading} onClick={onClose}>
            Cancel
          </Button>
          <Button color="danger" variant="solid" loading={loading} onClick={handleDelete}>
            Delete
          </Button>
        </>
      )}
    >
      Are you sure you want to delete <strong>{activity?.title}</strong>? All images and data will
      be permanently removed.
    </Modal>
  )
}
